import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeCanvas } from 'qrcode.react';
import { Button } from '../../components/ui/Button';
import { publicUrl, prettyUrl } from './publicUrl';

/**
 * QR for the venue's guest page. Encodes the SAME-ORIGIN guest URL
 * (`${origin}/v/{code}`) so the code is reachable in every environment (the
 * /v/:code route is served by this very SPA). The on-screen label shows the
 * same URL minus the scheme. Download PNG + Print so the owner can put it on
 * tables / the counter.
 *
 * ⚠️ The label used to be the LITERAL `popuppush.com/v/{code}` — a friendly
 * signpost for a host that serves the marketing site, not this SPA. Both the QR
 * and the label now come from `publicUrl`/`prettyUrl`, so the printed caption
 * always names the host the QR actually encodes.
 *
 * ⚠️ P5d — the encoded URL carries `?v=1`, the PHYSICAL-QR MARKER, and it is
 * what lets a guest scanning this printed code place an order at all. A BARE
 * /v/{code} is browse-only by design (it is indistinguishable from a bookmark or
 * a link someone was sent), so a code printed WITHOUT the marker can be read but
 * not ordered from.
 *
 * The label deliberately stays the friendly bare form: it is a human-readable
 * signpost, not the thing anyone scans, and `?v=1` on a poster is noise. Nobody
 * types this URL — and if they did, browse-only is the correct outcome.
 *
 * 📌 Venue QRs printed BEFORE this shipped are bare and must be re-printed. On
 * the prod cutover checklist; there is deliberately no grace period, because a
 * bare link from a poster and a bare link from a bookmark cannot be told apart.
 */
export default function QrCard({ code }) {
  const { t } = useTranslation();
  const wrapRef = useRef(null);

  const url = publicUrl(`/v/${code}?v=1`);
  const label = prettyUrl(`/v/${code}`);
  const canvas = () => wrapRef.current?.querySelector('canvas');

  const download = () => {
    const c = canvas();
    if (!c) return;
    const a = document.createElement('a');
    a.href = c.toDataURL('image/png');
    a.download = `${code}-qr.png`;
    a.click();
  };

  const print = () => {
    const c = canvas();
    if (!c) return;
    const img = c.toDataURL('image/png');
    const w = window.open('', '_blank', 'width=520,height=680');
    if (!w) return;
    w.document.write(
      `<html><head><title>${code}</title><meta charset="utf-8"><style>` +
        'body{font-family:system-ui,-apple-system,sans-serif;text-align:center;padding:48px 24px;color:#2a211b}' +
        'img{width:320px;height:320px}h1{font-size:24px;margin:20px 0 6px}p{color:#6e6154;font-size:15px;font-family:monospace}' +
        '</style></head><body>' +
        `<img src="${img}" alt="QR"/><h1>${t('loyalty.scan_to_join')}</h1><p>${label}</p>` +
        '<script>window.onload=function(){window.print();}</script></body></html>'
    );
    w.document.close();
  };

  return (
    <div className="lty-qr-wrap">
      <div ref={wrapRef} className="lty-qr-frame">
        <QRCodeCanvas value={url} size={200} level="M" marginSize={2} />
      </div>
      <div className="lty-qr-url">{label}</div>
      <div className="flex flex-wrap justify-center gap-2">
        <Button label={t('loyalty.download_qr')} icon="pi pi-download" onClick={download} outlined size="small" />
        <Button label={t('loyalty.print')} icon="pi pi-print" onClick={print} outlined size="small" />
      </div>
    </div>
  );
}
